import React, { useMemo, useState } from 'react';
import { SenseHint } from '../components/SenseChooser.js';
import {
  Button,
  Card,
  Flex,
  Input,
  Popconfirm,
  Popover,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, FileTextOutlined, SearchOutlined, SoundOutlined } from '@ant-design/icons';
import {
  Card as SRSCard,
  CardNoteSide,
  TranslationLanguage,
  displayIpa,
  spokenForm,
  translationFor,
} from '@lingoping/core';
import { CardImage } from '../components/CardImage.js';
import { CardNote } from '../components/CardNote.js';
import { cardImageUrl } from '../utils/images.js';
import { CHROMATIC_PALETTES } from '../theme/index.js';
import { GenderBadge } from '../components/GenderBadge.js';
import { TranslationText } from '../components/TranslationText.js';
import { speakText } from '../utils/audio.js';
import { cardStageLabel, formatNextReview, plural, visuallyHidden } from './format.js';
import { tourTarget } from './tourTargets.js';

const { Text } = Typography;

const STAGE_TAG_COLORS: Record<string, string> = {
  New: 'cyan',
  Review: 'green',
  Learning: 'gold',
  Relearning: 'red',
};

interface Props {
  cards: SRSCard[];
  /** The language the translation column and the examples are shown in. */
  translationLanguage?: TranslationLanguage;
  onDelete: (cardId: string) => Promise<void>;
  /** Saves the learner's own note for one side of a card (empty text removes it). */
  onSaveNote?: (cardId: string, side: CardNoteSide, text: string) => Promise<void>;
}

export const CardsTab: React.FC<Props> = ({
  cards,
  translationLanguage = 'en',
  onDelete,
  onSaveNote,
}) => {
  const [search, setSearch] = useState('');

  const filteredCards = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter(
      (c) =>
        c.front_text.toLowerCase().includes(q) ||
        (c.back_text?.toLowerCase().includes(q) ?? false) ||
        translationFor(c, translationLanguage).text.toLowerCase().includes(q) ||
        (c.lemma?.toLowerCase().includes(q) ?? false)
    );
  }, [cards, search, translationLanguage]);

  return (
    <Card
      {...tourTarget('cards')}
      title={
        <Flex align="baseline" gap={8} wrap>
          <span>Your cards</span>
          <Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>
            {plural(cards.length, 'card')}
          </Text>
        </Flex>
      }
    >
      <Flex style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search by word or translation"
          aria-label="Search your cards by word or translation"
          prefix={<SearchOutlined />}
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300, maxWidth: '100%' }}
        />
      </Flex>

      <Table
        dataSource={filteredCards}
        rowKey="id"
        pagination={{ pageSize: 8, hideOnSinglePage: true, showSizeChanger: false }}
        scroll={{ x: 560 }}
        locale={{
          emptyText: search
            ? 'No cards match your search.'
            : 'No cards yet. Follow a deck or add a word.',
        }}
        columns={[
          {
            title: 'Word',
            dataIndex: 'front_text',
            key: 'front_text',
            sorter: (a: SRSCard, b: SRSCard) => a.front_text.localeCompare(b.front_text),
            render: (text: string, record: SRSCard) => {
              const gender = record.linguistic_meta?.gender || 'neutral';
              const palette = CHROMATIC_PALETTES[gender] || CHROMATIC_PALETTES.neutral;
              return (
                <Space align="center" wrap>
                  <Text strong>
                    {text}
                    <SenseHint hint={record.sense_hint} />
                  </Text>
                  {record.linguistic_meta?.article && (
                    <GenderBadge article={record.linguistic_meta.article} palette={palette} />
                  )}
                  <Button
                    type="text"
                    size="small"
                    icon={<SoundOutlined style={{ color: palette.color }} />}
                    onClick={() =>
                      speakText(
                        spokenForm(text, record.linguistic_meta?.article),
                        record.target_language || 'de'
                      )
                    }
                    aria-label={`Hear “${text}”`}
                    title="Hear this word"
                  />
                  {(record.note_front || record.note_back) && (
                    <Popover
                      trigger={['hover', 'focus']}
                      title="Your notes"
                      content={
                        <div style={{ maxWidth: 320 }}>
                          {(['front', 'back'] as const).map((side) => {
                            const note = side === 'front' ? record.note_front : record.note_back;
                            return (
                              note && (
                                <div key={side} style={{ marginBottom: 6 }}>
                                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                    {side === 'front' ? 'Front' : 'Back'}
                                  </Text>
                                  <Text
                                    dir="auto"
                                    style={{
                                      whiteSpace: 'pre-wrap',
                                      overflowWrap: 'anywhere',
                                      display: 'block',
                                    }}
                                  >
                                    {note}
                                  </Text>
                                </div>
                              )
                            );
                          })}
                        </div>
                      }
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<FileTextOutlined style={{ color: '#b45309' }} />}
                        aria-label={`Your notes on “${text}”`}
                      />
                    </Popover>
                  )}
                </Space>
              );
            },
          },
          {
            title: 'Translation',
            key: 'back_text',
            render: (_: unknown, record: SRSCard) => {
              const shown = translationFor(record, translationLanguage);
              return <TranslationText as="span" text={shown.text} lang={shown.textLang} />;
            },
          },
          {
            title: 'Stage',
            dataIndex: 'fsrs_state',
            key: 'fsrs_state',
            width: 120,
            render: (s: string) => (
              <Tag color={STAGE_TAG_COLORS[s] ?? 'default'}>{cardStageLabel(s)}</Tag>
            ),
          },
          {
            title: 'Next review',
            dataIndex: 'due_date',
            key: 'due_date',
            width: 140,
            sorter: (a: SRSCard, b: SRSCard) => a.due_date.localeCompare(b.due_date),
            render: (d: string) => formatNextReview(d),
          },
          {
            title: <span style={visuallyHidden}>Delete</span>,
            key: 'action',
            width: 70,
            render: (_: unknown, record: SRSCard) => (
              <Popconfirm
                title={`Delete “${record.front_text}”?`}
                description="This removes the card from your deck."
                onConfirm={() => onDelete(record.id)}
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  aria-label={`Delete card ${record.front_text}`}
                />
              </Popconfirm>
            ),
          },
        ]}
        expandable={{
          expandedRowRender: (record: SRSCard) => (
            <div
              style={{
                padding: '10px 14px',
                backgroundColor: '#f8fafc',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
              }}
            >
              <Flex gap={16} align="start">
                {cardImageUrl(record) && (
                  <div style={{ width: 84, flexShrink: 0 }}>
                    <CardImage
                      src={cardImageUrl(record) as string}
                      alt={record.front_text}
                      height={84}
                      background="#e2e8f0"
                      border="1px solid #cbd5e1"
                      compact
                    />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {record.phonetic_ipa && (
                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                      Pronunciation: {displayIpa(record.phonetic_ipa)}
                    </Text>
                  )}
                  {record.example_target ? (
                    <div style={{ marginTop: 4 }}>
                      <Space align="baseline" wrap>
                        <Text strong style={{ color: '#0f172a' }}>
                          {record.example_target}
                        </Text>
                        <Button
                          type="text"
                          size="small"
                          icon={<SoundOutlined style={{ color: '#1677ff' }} />}
                          onClick={() =>
                            speakText(record.example_target || '', record.target_language || 'de')
                          }
                          aria-label="Hear the example sentence"
                          title="Hear the example sentence"
                        />
                      </Space>
                      {(() => {
                        const shown = translationFor(record, translationLanguage);
                        return (
                          shown.example && (
                            <TranslationText
                              text={shown.example}
                              lang={shown.exampleLang}
                              style={{ margin: '2px 0 0', fontSize: 13, color: '#5b6472' }}
                            />
                          )
                        );
                      })()}
                    </div>
                  ) : (
                    <Text type="secondary" italic>
                      No example sentence yet.
                    </Text>
                  )}
                </div>
              </Flex>
              {onSaveNote && (
                <Flex gap={12} wrap style={{ marginTop: 12 }}>
                  {(['front', 'back'] as const).map((side) => (
                    <div key={side} style={{ flex: '1 1 260px', minWidth: 0 }}>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                        {side === 'front' ? 'Front of the card' : 'Back of the card'}
                      </Text>
                      <CardNote
                        side={side}
                        value={side === 'front' ? record.note_front : record.note_back}
                        onSave={(text) => onSaveNote(record.id, side, text)}
                      />
                    </div>
                  ))}
                </Flex>
              )}
            </div>
          ),
        }}
      />
    </Card>
  );
};
